import React, { memo, useEffect, useRef, useState } from "react";

import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { FileUpload, FileUploadUploadEvent } from "primereact/fileupload";
import { Image } from "primereact/image";
import { InputNumber } from "primereact/inputnumber";
import { TabPanel, TabView } from "primereact/tabview";
import { T } from "services/lang/LanguageService";
import useComponentWidthStore from "store/component-width-store";
import useScadaStore from "store/scada-store";
import { DweenTimeUnitEnum } from "utils/time-utils";
import timeout from "utils/timeout";
import { isObjectEmpty } from "utils/utils";
import { v4 as uuidv4 } from "uuid";
import ScadaDatasetsTable from "../components/ScadaDatasetsTable";
import ScadaFreeTextTable from "../components/ScadaFreeTextTable";
import ScadaLabels from "../components/ScadaLabels";
import ScadaPolygonTable from "../components/ScadaPolygonTable";
import { ImagesUploadResponse, ScadaEditorProps, ScadaProps } from "../scadaTypes";
import { IMAGE_UPLOAD_URL } from "../utils/const";
import { defaultLayer, defaultScadaConfig } from "../utils/default-values";
import { getScadaImageUrl, tempId } from "../utils/scadaUtils";
import TextTagTable from "../components/TextTagTable";

const ScadaEditor: React.FC<ScadaEditorProps> = ({ config, onConfigChange }) => {
  const componentWidth = useComponentWidthStore(state => state.componentWidth);
  const [currentConfig, setCurrentConfig] = useState<ScadaProps>(() =>
    isObjectEmpty(config) ? { ...defaultScadaConfig, id: uuidv4() } : config
  );
  const downloadRef = useRef<HTMLAnchorElement>(null);
  const scadaId = config.id ?? tempId(config);
  const scada = useScadaStore(state => state.scadaMap.get(scadaId));
  const addScada = useScadaStore(state => state.addScada);

  const scadaLabels = scada?.labels;
  const scadaLabelLayers = scada?.labelLayers;
  const scadaPolygons = scada?.polygons;
  const ScadaFreeTexts = scada?.freeTexts;
  const scadaFreeTextTagTables = scada?.freeTextTagTables;

  useEffect(() => {
    if (componentWidth) {
      setCurrentConfig(prev => ({ ...prev, fontSizeScale: Math.round(1200 / componentWidth) / 100 }));
      onConfigChange({ ...currentConfig, fontSizeScale: Math.round(1200 / componentWidth) / 100 });
    }
  }, [componentWidth]);

  useEffect(() => {
    if (isObjectEmpty(config)) {
      onConfigChange(currentConfig);
      addScada({
        scadaId: currentConfig.id!,
        labels: currentConfig.elements,
        labelLayers: currentConfig.labelLayers,
        polygons: currentConfig.polygons,
        freeTexts: currentConfig.freeTexts,
        freeTextTagTables: currentConfig.freeTextTagTables,
      });
      return;
    }
    if (!scada) {
      onConfigChange({ ...config, id: currentConfig.id ?? scadaId });
      addScada({
        scadaId: currentConfig.id ?? scadaId,
        labels: config.elements ?? [],
        labelLayers: config.labelLayers ?? [defaultLayer],
        polygons: config.polygons ?? [],
        freeTexts: config.freeTexts ?? [],
        freeTextTagTables: config.freeTextTagTables ?? [],
      });
    } else {
      setCurrentConfig({
        ...config,
        elements: scadaLabels ?? [],
        labelLayers: scadaLabelLayers ?? [defaultLayer],
        polygons: scadaPolygons ?? [],
        freeTexts: ScadaFreeTexts ?? [],
        id: config.id ?? currentConfig.id,
        freeTextTagTables: scadaFreeTextTagTables ?? [],
      });
      onConfigChange({
        ...config,
        elements: scadaLabels ?? [],
        labelLayers: scadaLabelLayers ?? [defaultLayer],
        polygons: scadaPolygons ?? [],
        freeTexts: ScadaFreeTexts ?? [],
        id: config.id ?? currentConfig.id,
        freeTextTagTables: scadaFreeTextTagTables ?? [],
      });
    }
  }, []);

  const scadaBgUrl = getScadaImageUrl(config.scadaBackgroundId);

  const handleConfigChange = (
    value: string | number | boolean | null | undefined,
    field: keyof ScadaProps
  ) => {
    const newConfig = { ...currentConfig, [field]: value };

    timeout(() => {
      setCurrentConfig(newConfig);
      onConfigChange(newConfig);
    }, 200);
  };

  const onUpload = (e: FileUploadUploadEvent) => {
    const response: ImagesUploadResponse = JSON.parse(e.xhr.response);

    const newConfig = {
      ...currentConfig,
      scadaBackgroundId: response.id,
    };
    onConfigChange(newConfig);
    setCurrentConfig(newConfig);
    easasAlert({
      summary: T("alert.success"),
      severity: "success",
      detail: T("alert.image-uploaded"),
    });
  };

  const handleDownload = () => {
    downloadRef.current?.click();
  };

  return (
    <div className="grid p-fluid">
      <InputLabelWrapper labelName={T("label.scada.refresh-rate-in-sec")} col={4}>
        <InputNumber
          value={currentConfig.refreshRate}
          name="refreshRate"
          onChange={e => handleConfigChange(e.value, "refreshRate")}
          onValueChange={e => handleConfigChange(e.value, "refreshRate")}
          useGrouping={false}
          max={3600}
          min={0}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("label.scada.not-older-than")} col={4}>
        <div className="p-inputgroup">
          <InputNumber
            value={currentConfig.maxDelayTime}
            name="maxDelayTime"
            onChange={e => handleConfigChange(e.value, "maxDelayTime")}
            onValueChange={e => handleConfigChange(e.value, "maxDelayTime")}
            className="w-6 p-0"
            useGrouping={false}
            max={100}
            min={0}
          />
          <Dropdown
            value={currentConfig.maxDelayTimeUnit}
            options={Object.keys(DweenTimeUnitEnum)}
            name="maxDelayTimeUnit"
            onChange={e => handleConfigChange(e.value, "maxDelayTimeUnit")}
            className="p-inputgroup-addon w-6 p-0"
          />
        </div>
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("label.scada.label-truncate-size")} col={4}>
        <InputNumber
          value={currentConfig.truncateSize}
          name="truncateSize"
          onChange={e => handleConfigChange(e.value, "truncateSize")}
          onValueChange={e => handleConfigChange(e.value, "truncateSize")}
          useGrouping={false}
          max={100}
          min={0}
        />
      </InputLabelWrapper>
      <InputLabelWrapper
        labelName={T("label.scada.show-scada-settings-header")}
        col={8}
        flexDirection="row-reverse"
        className="justify-content-end align-items-center gap-3"
      >
        <Checkbox
          checked={currentConfig.showScadaSettings}
          name="showScadaSettings"
          onChange={e => handleConfigChange(e.target.checked, "showScadaSettings")}
          className="inline-block"
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("label.font-size-scale")} col={4}>
        <InputNumber
          value={currentConfig.fontSizeScale}
          name="fontSizeScale"
          onValueChange={e => handleConfigChange(e.value, "fontSizeScale")}
          placeholder="1"
          useGrouping={false}
          maxFractionDigits={3}
          max={100}
          min={0}
        />
      </InputLabelWrapper>

      <InputLabelWrapper labelName="" col={4}>
        <FileUpload
          name="file"
          mode="basic"
          url={IMAGE_UPLOAD_URL}
          auto
          chooseOptions={{
            label: T("label.scada.upload-scada-background"),
            icon: "pi pi-upload",
          }}
          onUpload={onUpload}
          accept="image/*"
        />
      </InputLabelWrapper>
      {scadaBgUrl && (
        <InputLabelWrapper labelName="" col={8} className="justify-content-end gap-5" flexDirection="row">
          <Image
            src={scadaBgUrl}
            alt={T("alt.scada.scada-background-image")}
            height="35"
            style={{ objectFit: "contain" }}
            preview
          />
          <Button
            onClick={handleDownload}
            type="button"
            label={T("action.download")}
            icon="pi pi-download"
            className="p-button-warning w-12rem"
          />
          <a
            ref={downloadRef}
            style={{ display: "none" }}
            href={`${scadaBgUrl}?download=${Math.random()}`}
            title={T("action.download-image")}
          />
        </InputLabelWrapper>
      )}
      <TabView activeIndex={0} className="w-full">
        <TabPanel header={T("word.tags")}>
          <ScadaDatasetsTable
            currentConfig={currentConfig}
            setCurrentConfig={setCurrentConfig}
            config={config}
            onConfigChange={onConfigChange}
          />
        </TabPanel>
        <TabPanel header={T("header.scada.label-layers")}>
          <ScadaLabels
            currentConfig={currentConfig}
            setCurrentConfig={setCurrentConfig}
            onConfigChange={onConfigChange}
          />
        </TabPanel>
        <TabPanel header={T("word.polygons")}>
          <ScadaPolygonTable
            currentConfig={currentConfig}
            setCurrentConfig={setCurrentConfig}
            onConfigChange={onConfigChange}
          />
        </TabPanel>
        <TabPanel header={T("label.free-text")}>
          <ScadaFreeTextTable
            currentConfig={currentConfig}
            setCurrentConfig={setCurrentConfig}
            onConfigChange={onConfigChange}
          />
        </TabPanel>
        <TabPanel header={T("header.free-text-tag-table")}>
          <TextTagTable
            currentConfig={currentConfig}
            setCurrentConfig={setCurrentConfig}
            onConfigChange={onConfigChange}
          />
        </TabPanel>
      </TabView>
    </div>
  );
};

export default memo(ScadaEditor, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config);
});
